import React from 'react';
import type {RemoteCursorsOverlayProps} from '@/types/components/remoteCursors.types';
import {graphLocalToClient} from '@/utils/diagram/graphClientPoint';
import {colorForUserId} from '@/utils/diagram/userColor';
import './RemoteCursorsOverlay.css';

const RemoteCursorsOverlay: React.FC<RemoteCursorsOverlayProps> = ({
	cursors,
	activeViewId,
	containerRef,
	modelerRef,
	modelerReady,
	viewReady,
}) => {
	const [, setTransformTick] = React.useState(0);

	React.useEffect(() => {
		if (!modelerReady || !viewReady) return;
		const graph = modelerRef.current?.getGraph?.();
		const container = containerRef.current;
		const bump = () => setTransformTick(tick => tick + 1);
		graph?.on?.('scale', bump);
		graph?.on?.('translate', bump);
		graph?.on?.('resize', bump);
		container?.addEventListener('wheel', bump, {passive: true});
		const onPointerMove = (event: PointerEvent) => {
			if (event.buttons) bump();
		};
		container?.addEventListener('pointermove', onPointerMove);
		window.addEventListener('resize', bump);
		return () => {
			graph?.off?.('scale', bump);
			graph?.off?.('translate', bump);
			graph?.off?.('resize', bump);
			container?.removeEventListener('wheel', bump);
			container?.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('resize', bump);
		};
	}, [modelerReady, viewReady, modelerRef, containerRef]);

	const visible = React.useMemo(
		() =>
			cursors.filter(
				cursor => cursor.viewId != null && activeViewId != null && cursor.viewId === activeViewId
			),
		[cursors, activeViewId]
	);

	if (!visible.length) return null;

	const graph = modelerRef.current?.getGraph?.();
	const container = containerRef.current;
	const origin = container?.getBoundingClientRect();

	return (
		<div className="remote-cursors-overlay" aria-hidden>
			{visible.map(cursor => {
				const client = graphLocalToClient(graph, cursor.x, cursor.y);
				if (!client || !origin) return null;
				const left = client.x - origin.left;
				const top = client.y - origin.top;
				const color = colorForUserId(cursor.userId);
				const name = cursor.userName?.trim() || 'Участник';
				return (
					<div
						key={cursor.clientSessionId}
						className="remote-cursor"
						style={{transform: `translate(${left}px, ${top}px)`}}
					>
						<svg
							className="remote-cursor__pointer"
							width="18"
							height="22"
							viewBox="0 0 18 22"
							aria-hidden
						>
							<path
								d="M1.2 1.2 L1.2 16.8 L5.6 13.1 L8.8 20.6 L12.1 19.2 L8.7 11.5 L15.2 11.5 Z"
								fill={color}
								stroke="#fff"
								strokeWidth="1.2"
								strokeLinejoin="round"
							/>
						</svg>
						<div className="remote-cursor__label" style={{background: color}} title={name}>
							{name}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default React.memo(RemoteCursorsOverlay);
